import {
  EntityType,
  GetExamCourseCommentsDocument,
  useGetDownloadSignedUrlForExamCourseComment,
  useGetUploadSignedUrlForExamCourseComment,
  useSendExamCourseComment,
} from '@exam-room/graphql/preview';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { Button, FormRichTextEditor } from '../../../../components';

interface CommentFormProps {
  questionId: string;
  taskId: string;
}

export default function CommentForm(props: CommentFormProps) {
  const { questionId, taskId } = props;

  const [searchParams] = useSearchParams();

  const formContext = useForm({
    defaultValues: {
      'review-comment': '',
    },
  });

  // Watch for question ID or page changes to clear the form
  const questionIdFromParams = searchParams.get('q');
  const pageFromParams = searchParams.get('page');

  useEffect(() => {
    // Clear the comment form when navigating to a different question or page
    formContext.reset({ 'review-comment': '' });
  }, [questionIdFromParams, pageFromParams, formContext]);

  const [isUploading, setIsUploading] = useState(false);

  const [sendComment, { loading }] = useSendExamCourseComment();

  const [getUploadSignedUrl] = useGetUploadSignedUrlForExamCourseComment({
    fetchPolicy: 'no-cache',
  });
  const [getDownloadSignedUrl] = useGetDownloadSignedUrlForExamCourseComment({
    fetchPolicy: 'no-cache',
  });

  async function handleUpload(file: File) {
    setIsUploading(true);
    try {
      const { data } = await getUploadSignedUrl({
        variables: {
          getUploadSignedUrlForExamCourseCommentInput: {
            contentType: file.type,
            taskId,
          },
        },
      });
      if (!data?.excGetUploadSignedUrlForExamCourseComment.signedUrl) {
        throw new Error('Cannot get signed url for upload image');
      }

      const { signedUrl } = data.excGetUploadSignedUrlForExamCourseComment;

      await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      const { data: getDownloadSignedUrlData } = await getDownloadSignedUrl({
        variables: {
          getDownloadSignedUrlForExamCourseCommentInput: {
            fileNames: [
              data.excGetUploadSignedUrlForExamCourseComment.storageObject.fileName ?? '',
            ],
            taskId,
          },
        },
      });

      const fileName = data.excGetUploadSignedUrlForExamCourseComment.storageObject.fileName ?? '';

      const result = {
        url: getDownloadSignedUrlData?.excGetDownloadSignedUrlForExamCourseComment.signedUrls[
          fileName
        ] as string,
        fileName: fileName,
      };

      return result;
    } catch (error) {
      console.error('Error uploading image:', error);
      return undefined;
    } finally {
      setIsUploading(false);
    }
  }

  function parseCommentContent(content: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    const imgElements = doc.getElementsByTagName('img');

    for (const img of Array.from(imgElements)) {
      if (img.getAttribute('content') && img.hasAttribute('src')) {
        img.removeAttribute('src');
      }
    }

    return doc.body.innerHTML;
  }

  const onSubmit = formContext.handleSubmit(async (data) => {
    if (loading) return;
    if (!questionId || !taskId) return;

    await sendComment({
      variables: {
        createExamCourseCommentInput: {
          content: parseCommentContent(data['review-comment']),
          entityId: questionId,
          entityType: EntityType.Question,
          taskId: taskId,
        },
      },
      refetchQueries: [
        {
          query: GetExamCourseCommentsDocument,
          variables: {
            getExamCourseCommentsInput: {
              entityId: questionId,
              entityType: EntityType.Question,
              taskId: taskId,
            },
          },
        },
      ],
      onCompleted: () => {
        formContext.reset({ 'review-comment': '' });
      },
    });
  });

  const commentContent = formContext.watch('review-comment').trim();

  return (
    <div className='flex w-full flex-col items-start gap-2'>
      <div className='w-full rounded-lg border border-gray-200'>
        <FormProvider {...formContext}>
          <FormRichTextEditor
            name='review-comment'
            placeholder='Add a Comment...'
            rows={2}
            onImageUpload={handleUpload}
            loading={loading || isUploading}
          />
        </FormProvider>
        <div className='flex justify-end border-t border-gray-200 p-3'>
          <Button
            type='button'
            onClick={onSubmit}
            disabled={commentContent.length === 0 || commentContent === '<p></p>' || loading}
          >
            Add Comment
          </Button>
        </div>
      </div>
    </div>
  );
}
